<template>
  <div class="p2p-page">
    <div class="p2p-between">
      <div class="p2p-stack" style="gap:6px">
        <h1 class="p2p-h1">Projects</h1>
        <p class="p2p-lead">Every product you promote, with its posts, schedule and autopilot.</p>
      </div>
      <router-link to="/create" class="p2p-btn"><v-icon small color="#0b0b10">mdi-plus</v-icon> New project</router-link>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="paymentDue" class="p2p-card is-accent p2p-plan-bar">
      <div class="p2p-stack" style="gap:4px">
        <h2 class="p2p-h2">Your payment failed</h2>
        <p class="p2p-text">Fix your payment method to keep posting. Your projects and posts are safe.</p>
      </div>
      <router-link to="/subscription" class="p2p-btn" style="flex:0 0 auto">Fix payment method</router-link>
    </div>
    <div v-else-if="needsPlan" class="p2p-card is-accent p2p-plan-bar">
      <div class="p2p-stack" style="gap:4px">
        <h2 class="p2p-h2">Choose a plan to get your posts</h2>
        <p class="p2p-text">Every plan reads your site, writes and designs posts, and publishes them on your schedule. 30-day money-back guarantee.</p>
      </div>
      <router-link :to="{ path: '/subscribe', query: { next: '/create' } }" class="p2p-btn" style="flex:0 0 auto">Choose a plan</router-link>
    </div>
    <div v-if="workspace && (workspace.autopilots_active || pausedTogether)" class="p2p-card tight p2p-autopilot-bar">
      <div class="p2p-row" style="gap:10px">
        <span :class="['p2p-dot', workspace.autopilots_active ? 'is-accent' : 'warn']"></span>
        <span v-if="workspace.autopilots_active">{{ workspace.autopilots_active }} autopilot{{ workspace.autopilots_active === 1 ? '' : 's' }} on<span v-if="workspace.next_autopilot_at" class="p2p-faint"> · next post {{ when(workspace.next_autopilot_at) }}</span></span>
        <span v-else>All autopilots are paused</span>
      </div>
      <button v-if="workspace.autopilots_active" type="button" class="p2p-btn sm soft" :disabled="busy" @click="setAll('paused')">Pause all</button>
      <button v-else type="button" class="p2p-btn sm" :disabled="busy" @click="setAll('active')">Resume all</button>
    </div>
    <div v-if="loading" class="p2p-grid"><div v-for="n in 3" :key="n" class="p2p-skeleton" style="height:200px"></div></div>
    <section v-else-if="!projects.length" class="p2p-hero">
      <h2 class="p2p-h1">Start with your product link</h2>
      <p class="p2p-lead">We read it and write posts you can publish today. No setup.</p>
      <form class="p2p-urlbar" @submit.prevent="$router.push({ path: '/start', query: { url } })">
        <input v-model="url" type="text" inputmode="url" placeholder="yourproduct.com" aria-label="Website address">
        <button type="submit" class="p2p-btn lg" :disabled="!url.trim()">Read my site</button>
      </form>
    </section>
    <div v-else class="p2p-grid">
      <router-link v-for="project in projects" :key="project.id" :to="'/projects/' + project.id" class="p2p-card p2p-project">
        <div class="p2p-row" style="flex-wrap:nowrap;gap:12px">
          <MediaImg v-if="project.product && project.product.image_url" :src="project.product.image_url" ratio="1" class="p2p-project-logo" />
          <span v-else class="p2p-project-logo empty"><v-icon small>mdi-web</v-icon></span>
          <div style="min-width:0">
            <div class="p2p-h3" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ project.name }}</div>
            <div class="p2p-faint p2p-mono" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ host(project.primary_url) || 'No website' }}</div>
          </div>
        </div>
        <p class="p2p-muted" style="margin:0;min-height:38px">{{ project.product ? project.product.one_liner : 'Not read yet' }}</p>
        <div class="p2p-row" style="gap:6px">
          <span class="p2p-chip mono">{{ project.stats.posts }} posts</span>
          <span v-if="project.stats.scheduled" class="p2p-chip mono">{{ project.stats.scheduled }} scheduled</span>
          <span v-if="project.stats.published" class="p2p-chip good mono">{{ project.stats.published }} published</span>
          <span v-if="project.stats.failed" class="p2p-chip bad mono">{{ project.stats.failed }} failed</span>
        </div>
        <div class="p2p-row p2p-faint" style="gap:8px">
          <template v-if="project.automation && project.automation.status === 'active'">
            <span class="p2p-dot is-accent"></span>Autopilot on<span v-if="project.automation.next_slot_at"> · next {{ when(project.automation.next_slot_at) }}</span>
          </template>
          <template v-else-if="project.automation"><span class="p2p-dot warn"></span>Autopilot paused</template>
          <template v-else><span class="p2p-dot"></span>Updated {{ when(project.updated_at) }}</template>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script>
import MediaImg from '../components/MediaImg.vue';
import { get, post, message } from '../api';
import { localDate, hostOf } from '../poll';

// True when the plan picker was already shown in this browser tab (so Projects is not a dead end).
function planOffered() {
  try {
    if (sessionStorage.getItem('p2p-plan-offered')) return true;
    sessionStorage.setItem('p2p-plan-offered', '1');
  } catch (error) { /* Storage unavailable: offer it every time. */ }
  return false;
}

export default {
  name: 'Projects',
  components: { MediaImg },
  data: () => ({ projects: [], loading: true, error: '', url: '', busy: false }),
  computed: {
    workspace() { return this.$store.getters['publisher/workspace']; },
    // A paid-only site: a member without a plan can look around, and is asked to choose one.
    // The server's one access rule: 'none' needs a plan, 'payment_failed' needs the payment method fixed.
    needsPlan() { return !!(this.workspace && this.workspace.member && this.workspace.access === 'none' && this.$store.getters.forcePaidPlans); },
    paymentDue() { return !!(this.workspace && this.workspace.member && this.workspace.access === 'payment_failed'); },
    pausedTogether() { return this.projects.some(project => project.automation && project.automation.status === 'paused' && project.automation.pause_reason === 'paused_all'); }
  },
  mounted() { this.load(); },
  methods: {
    host: hostOf,
    when(value) { return localDate(value, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); },
    async load() {
      try {
        this.projects = (await get('/projects')).projects;
        // A new member without a plan sees the plan picker first, once per visit; afterwards they can look around.
        await this.$store.dispatch('publisher/loadWorkspace');
        if (this.needsPlan && !this.projects.length && !planOffered()) {
          this.$router.replace({ path: '/subscribe', query: { next: '/create' } }).catch(() => {});
        }
      } catch (error) {
        this.error = message(error);
      } finally {
        this.loading = false;
      }
    },
    async setAll(status) {
      this.busy = true;
      this.error = '';
      try {
        await post('/automations/all', { status });
        await Promise.all([this.load(), this.$store.dispatch('publisher/loadWorkspace')]);
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-project{text-decoration:none!important;color:inherit!important;transition:border-color .15s,background .15s}
.p2p-project:hover{border-color:var(--p2p-line-strong);background:var(--p2p-card)}
.p2p-project-logo{flex:0 0 auto;width:44px!important;height:44px;border-radius:11px;overflow:hidden;border:1px solid var(--p2p-input-line)}
.p2p-project-logo.empty{display:inline-grid;place-items:center;background:var(--p2p-raised);color:var(--p2p-muted)}
.p2p-autopilot-bar,.p2p-plan-bar{flex-direction:row!important;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px}
</style>
